import { useState, useCallback, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { getFeature } from '../../DaEditorWrapper/Features/helper';
import { IS_EMPTY_FEATURE } from '../../DaEditorWrapper/Features';
import { isAndroid } from '../../utils/browser';
const useEditor = ({ onEditorReady , onEditorInitialContentReady , onEditorChange , onFocus , onBlur , autoFocus  } = {})=>{
    const [editorApi, setEditorApi] = useState(null);
    const [editorState, setEditorState] = useState(null);
    const [editorToolbars, setEditorToolbars] = useState(null);
    const handleEditorReady = useCallback((toolbars, api)=>{
        setEditorToolbars(toolbars);
        setEditorApi(api);
        onEditorReady && onEditorReady(toolbars, api);
        if (autoFocus && typeof window !== 'undefined') {
            window.requestAnimationFrame(()=>{
                api.focusEditor();
            });
        }
    }, [
        autoFocus,
        onEditorReady
    ]);
    const handleEditorChange = useCallback((rawContentState, editorContent, features)=>{
        // When adding new entities, the change might not be reflected on `editorContent`, so to be safe we make a
        // deep comparison on the `rawContentState`.
        // Comparing `editorContent` is still useful because it's faster and will short circuit in many cases before
        // we get to the isEqual call.
        const contentChanged = !editorState || !editorState.editorContent.equals(editorContent) || !isEqual(editorState.rawContentState, rawContentState);
        let newState;
        if (contentChanged) {
            const { isEmpty  } = getFeature(IS_EMPTY_FEATURE, features).data;
            newState = {
                rawContentState,
                editorContent,
                features,
                isEmpty: isAndroid() ? false : isEmpty
            };
            setEditorState(newState);
        }
        onEditorChange && onEditorChange(newState || editorState);
    }, [
        editorState,
        onEditorChange
    ]);
    const handleOverrideEditor = useCallback((newEditorState)=>{
        setEditorState(newEditorState);
    }, []);
    const editorProps = useMemo(()=>({
            onEditorInit: handleEditorReady,
            onContentChange: handleEditorChange,
            onInitialContentReady: onEditorInitialContentReady,
            onFocus: onFocus,
            onBlur: onBlur
        }), [
        handleEditorReady,
        handleEditorChange,
        onEditorInitialContentReady,
        onFocus,
        onBlur
    ]);
    return {
        editorApi,
        editorState,
        editorToolbars,
        editorProps,
        /**
     * In rare cases, we may need to override the editor state completely.
     *
     * Currently, the only one we need to do that for is our fallback editors.
     */ overrideEditorState: handleOverrideEditor
    };
};
export default useEditor;
